import * as THREE from 'three';
import { basic, emis, std } from '../materials.ts';
import { G, M, V, glowSprite } from '../prims.ts';
import { kit } from './era4-kit.ts';
import { begin, bonesOf, done, joint, type BaseOpts, type Model } from './types.ts';

/*
 * La Madre de las Sanguijuelas (the Chasm's Black Cisterns guardian, docs/SIMA.md «Guardianes»): «Pegale cuando
 * baja el agua.» A leech-matriarch as long as a cart, bloated and glistening, that lies in the pool in the middle
 * of the room. Seven swollen rings of wet black-green skin, a pale belly, two rows of teal lights down her back,
 * a mane of drowned weed, little leeches clinging to her flanks; at the front her neck rears into a round sucker
 * mouth ringed with three circles of grey teeth and a teal throat.
 *
 * How the integrator drives her (anim/creatures/leech-mother.ts):
 * - The rest pose (what the gallery shows) is her bare, out of the water. Her clips put the water in:
 *   'idle' and 'walk' are submerged (only the hump of her back and the reared neck above the floor: the pool
 *   plane hides the rest), 'attack' bites from the water and 'hit' flinches in it.
 * - When the water drains: 'rise' (1.2 s, from submerged to bare), then loop 'exposed' (lying bare, writhing),
 *   'hitBare' for blows meanwhile, 'spit' (a spit/bite out of the water's reach), and 'sink' (1.2 s) when it rises.
 * - 'stun' and 'die' are bare.
 *
 * Bones: body (the bloated middle ring), tail1–tail3 behind it in a chain, neck1, neck2, head in front, `mouth`
 * (the sucker's lip and teeth; the clips scale it to open). Faces +Z. Opaque shared materials, the goo the one
 * translucent unlit one, plus glow sprites (no lights).
 */

export const LEECH_HEIGHT = 2.2;

export const leechMother = (o?: BaseOpts): Model => {
  const c = begin(o, 1.35, 4401);
  const { g, rng } = c;
  const k4 = kit(rng);

  const skin = std('#1c2b29', { roughness: 0.26, metalness: 0.12, bumpScale: 0.03 });
  const fold = std('#0e1615', { roughness: 0.32, metalness: 0.1 });
  const belly = std('#4b6863', { roughness: 0.42, bumpScale: 0.025 });
  const glow = emis('#86e8d2', '#5fd0b8', 1.3);
  const teeth = std('#a3aab0', { roughness: 0.5, flatShading: true });
  const inside = std('#123029', { roughness: 0.3, side: THREE.DoubleSide });
  const weed = std('#1a261c', { roughness: 0.85, bumpScale: 0.04 });
  const goo = basic('#6fdcc4', 0.32);

  // The spine, tail to head: where each ring sits, how fat it is.
  const P = [V(0, 0.22, -1.7), V(0, 0.34, -1.18), V(0, 0.46, -0.56), V(0, 0.54, 0.1), V(0, 0.62, 0.72), V(0, 1.0, 1.12), V(0, 1.5, 1.3)];
  const RAD = [0.27, 0.43, 0.6, 0.72, 0.6, 0.44, 0.38];

  /** One swollen ring: a glistening flattened swell, its pale belly, fine dark creases across it. */
  const ring = (parent: THREE.Object3D, i: number): void => {
    const p = P[i]!, r = RAD[i]!;
    const next = P[i + 1] ?? P[i - 1]!;
    const dir = i >= 5 ? next.clone().sub(p).normalize() : V(0, 0, 1);
    const q = new THREE.Quaternion().setFromUnitVectors(V(0, 0, 1), dir);
    const rx = r * 1.28, ry = r * 0.74, rz = r * 1.02;
    const s = M(new THREE.SphereGeometry(1, 11, 7), skin, parent);
    s.position.copy(p);
    s.scale.set(rx, ry, rz);
    s.quaternion.copy(q);
    const b = M(new THREE.SphereGeometry(1, 8, 5), belly, parent);
    b.position.copy(p).add(V(0, -r * 0.24, 0));
    b.scale.set(rx * 0.92, ry * 0.72, rz * 0.9);
    b.quaternion.copy(q);
    // Creases: thin dark rings hugging the swell.
    for (const f of [-0.45, 0.2]) {
      const k = Math.sqrt(1 - f * f);
      const cr = M(new THREE.TorusGeometry(1, 0.016, 3, 16), fold, parent);
      cr.position.copy(p).add(V(0, 0, rz * f).applyQuaternion(q));
      cr.scale.set(rx * k * 1.005, ry * k * 1.005, 1);
      cr.quaternion.copy(q);
    }
    // Two rows of teal lights down the back, and the low ridge between them.
    const n = i === 0 ? 1 : i >= 5 ? 1 : 2;
    for (let j = 0; j < n; j++) {
      const dz = (j - (n - 1) / 2) * r * 0.7;
      for (const k of [-1, 1]) {
        const d = M(new THREE.SphereGeometry(1, 5, 3), glow, parent);
        d.position.copy(p).add(V(k * rx * 0.36, ry * 0.86, dz).applyQuaternion(q));
        d.scale.set(r * 0.1, r * 0.05, r * 0.2);
        d.quaternion.copy(q);
      }
      const bump = M(new THREE.SphereGeometry(1, 5, 3), fold, parent);
      bump.position.copy(p).add(V(0, ry * 0.97, dz + r * 0.3).applyQuaternion(q));
      bump.scale.set(r * 0.12, r * 0.07, r * 0.16);
    }
    if (i >= 1 && i <= 4) glowSprite(parent, '#5fd0b8', r * 1.4, p.clone().add(V(0, ry * 0.9, 0)), 0.2);
  };

  /** A leech clinging to her flank: a dark teardrop pressed to the skin, its head lifted, a teal tip. */
  const clinger = (parent: THREE.Object3D, at: THREE.Vector3, k: number, len: number): void => {
    const body = M(new THREE.SphereGeometry(1, 8, 5), skin, parent);
    body.position.copy(at);
    body.scale.set(len * 0.2, len * 0.55, len * 0.24);
    body.rotation.set(0.3 * k, 0, -0.5 * k);
    const head = M(new THREE.SphereGeometry(1, 6, 4), fold, parent);
    head.position.copy(at).add(V(k * len * 0.22, len * 0.42, 0.02));
    head.scale.setScalar(len * 0.12);
    const tip = M(new THREE.SphereGeometry(0.03, 5, 3), glow, parent);
    tip.position.copy(at).add(V(k * len * 0.3, len * 0.5, 0.04));
  };

  /** Drowned weed draped over her: a dark limp strand. */
  const strand = (parent: THREE.Object3D, from: THREE.Vector3, k: number, len: number): void => {
    const pts = [from, from.clone().add(V(k * len * 0.35, -len * 0.25, 0.05)), from.clone().add(V(k * len * 0.6, -len * 0.65, -0.03)), from.clone().add(V(k * len * 0.66, -len, 0.04))];
    k4.tube(parent, pts, 0.022, weed, 4);
  };

  /* ---------- the bloated middle and the tail behind it ---------- */
  const bodyB = joint(g, P[3]!, 'body');
  ring(bodyB.abs, 3);
  const t1 = joint(bodyB.abs, P[2]!.clone().lerp(P[3]!, 0.4), 'tail1');
  ring(t1.abs, 2);
  const t2 = joint(t1.abs, P[1]!.clone().lerp(P[2]!, 0.4), 'tail2');
  ring(t2.abs, 1);
  const t3 = joint(t2.abs, P[0]!.clone().lerp(P[1]!, 0.4), 'tail3');
  ring(t3.abs, 0);
  // The tail ends in a flat round sucker, pressed to the floor.
  const tailSucker = M(new THREE.CylinderGeometry(0.2, 0.24, 0.07, 12), fold, t3.abs);
  tailSucker.position.set(0, 0.06, -1.95);

  for (const [parent, i, k, len] of [[bodyB.abs, 3, 1, 0.34], [bodyB.abs, 3, -1, 0.28], [t1.abs, 2, -1, 0.32], [t2.abs, 1, 1, 0.24]] as const) {
    const p = P[i]!, r = RAD[i]!;
    clinger(parent, p.clone().add(V(k * r * 1.27, r * 0.05, (rng() - 0.5) * r * 0.6)), k, len);
  }
  // Weed over the hump, hanging both sides.
  for (let i = 0; i < 6; i++) {
    const k = i % 2 ? 1 : -1;
    const z = -0.3 + i * 0.2;
    strand(bodyB.abs, V(k * 0.28, 1.06 - Math.abs(z) * 0.08, z), k, 0.5 + rng() * 0.2);
  }

  /* ---------- the neck rearing and the sucker head ---------- */
  const n1 = joint(bodyB.abs, P[4]!.clone().lerp(P[3]!, 0.4), 'neck1');
  ring(n1.abs, 4);
  const n2 = joint(n1.abs, P[5]!.clone().lerp(P[4]!, 0.45), 'neck2');
  ring(n2.abs, 5);
  clinger(n2.abs, P[5]!.clone().add(V(-0.52, 0.05, 0)), -1, 0.22);
  const hB = joint(n2.abs, P[6]!.clone().lerp(P[5]!, 0.4), 'head');
  const hd = hB.abs;
  ring(hd, 6);
  // The head: a blunt cone toward the mouth, a crown of teal eye-spots round its top.
  const front = V(0, 1.86, 1.74);
  const fdir = V(0, 0.3, 1).normalize();
  const cone = M(new THREE.SphereGeometry(1, 12, 8), skin, hd);
  cone.position.copy(P[6]!).lerp(front, 0.3);
  cone.scale.set(0.45, 0.4, 0.4);
  cone.quaternion.setFromUnitVectors(V(0, 0, 1), fdir);
  for (let i = 0; i < 7; i++) {
    const a = -1.2 + i * 0.4;
    const e = M(new THREE.SphereGeometry(0.03, 6, 4), glow, hd);
    e.position.copy(front).add(V(Math.sin(a) * 0.3, 0.2 + Math.cos(a) * 0.05, -0.12));
  }
  glowSprite(hd, '#7fe8d2', 0.5, front.clone().add(V(0, 0.22, -0.08)), 0.35);
  // Goo dripping off the chin.
  for (const k of [-1, 0, 1]) {
    const d = M(new THREE.ConeGeometry(0.035, 0.32 + Math.abs(k) * -0.1, 5), goo, hd);
    d.position.copy(front).add(V(k * 0.16, -0.38, 0.02));
    d.rotation.x = Math.PI;
  }

  /* ---------- the mouth: lip, three rings of teeth, the throat ---------- */
  const mB = joint(hd, front, 'mouth');
  const mg = G(mB.abs, front.x, front.y, front.z);
  mg.quaternion.setFromUnitVectors(V(0, 0, 1), fdir);
  // The sucker: a flared funnel of dark skin, rolled at its rim, down to the teal throat.
  const funnel = M(new THREE.LatheGeometry([new THREE.Vector2(0.07, -0.2), new THREE.Vector2(0.14, -0.12), new THREE.Vector2(0.25, -0.03), new THREE.Vector2(0.34, 0.03), new THREE.Vector2(0.37, 0.07)], 18), inside, mg);
  funnel.rotation.x = Math.PI / 2;
  const lip = M(new THREE.TorusGeometry(0.36, 0.05, 5, 22), skin, mg);
  lip.position.z = 0.06;
  const gullet = M(new THREE.SphereGeometry(0.08, 6, 4), glow, mg);
  gullet.position.z = -0.2;
  glowSprite(mg, '#5fd0b8', 0.7, V(0, 0, -0.1), 0.55);
  for (const [r, n, len, z] of [[0.31, 14, 0.12, 0.02], [0.23, 11, 0.1, -0.04], [0.15, 8, 0.08, -0.1]] as const) {
    for (let i = 0; i < n; i++) {
      const a = (i / n) * Math.PI * 2 + r * 7;
      const tt = M(new THREE.ConeGeometry(0.02 + len * 0.18, len, 4), teeth, mg);
      tt.position.set(Math.cos(a) * r, Math.sin(a) * r, z);
      // Pointing in toward the throat and back down it.
      tt.quaternion.setFromUnitVectors(V(0, 1, 0), V(-Math.cos(a), -Math.sin(a), -0.35).normalize());
    }
  }

  return done(c, LEECH_HEIGHT, () => undefined, undefined, bonesOf(c, {
    body: bodyB.p, tail1: t1.p, tail2: t2.p, tail3: t3.p, neck1: n1.p, neck2: n2.p, head: hB.p, mouth: mB.p,
  }));
};
